import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import { uploadFileV2 } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { MiniDocEditor } from "@toddle-edu/ds-doc-editor";
import { FormInputError } from "@toddle-edu/ds-web";
import { getCloudUrl } from "Constants";
import MathEquationEditor from "../MathEquationEditor";
import docEditorStore from "./docEditorStore";

import { allowURLWithCredentials } from "../../Utils/SecureUrlHelpers";

class MiniDocEditorComponent extends UIBaseComponent {
  static openEquationEditorInstance = null;
  errorId = _.uniqueId("form-error-");

  state = {
    ...this.state,
    hasContent: false,
    isEditorLoading: false,
    uploadIds: [],
    showUploadConfirmation: false,
    pendingUnmountCallback: null,
  };
  containerRef = React.createRef();

  changeValue = (val, isEmpty) => {
    this.updateValue(val, isEmpty);
  };

  isValid = () => {
    const { error } = this.props;
    const errorMessage = error ? error : "Enter some content";

    if (!this.state.hasContent) {
      this.updateErrorState(errorMessage);
    } else {
      this.updateErrorState("");
    }
    return this.state.hasContent ? 0 : 1;
  };

  _onBlur = () => {
    const { value, shouldCallOnBlur } = this.props;
    if (shouldCallOnBlur) {
      this.onBlur({ value });
    }
  };

  closeEditor = () => {
    if (MiniDocEditorComponent.openEquationEditorInstance === this) {
      MiniDocEditorComponent.openEquationEditorInstance = null;
    }

    this.setState({
      isEquationEditorOpen: false,
      latex: "",
      latexList: [],
    });
  };

  handleInsertEquation = ({ height, width, latex, verticalAlign, src }) => {
    const equationAttributes = {
      src,
      height: height + "px",
      width: width + "px",
      verticalAlign: verticalAlign + "px",
      latex,
      altText: latex,
      display: "inline",
    };

    this.setState({ equationAttributes });
  };

  renderEquationEditor = () => {
    const {
      allowCopyPasteInEquationEditor = true,
      disabledCopyButtonTooltipText = null,
    } = this.props;
    const latexList = this.state.latexList;
    return (
      <MathEquationEditor
        closeEditor={this.closeEditor}
        onInsertButtonClick={this.handleInsertEquation}
        latex={this.state.latex}
        latexList={
          this.state.allowReInsertImageAutomation && latexList?.length > 0
            ? latexList
            : null
        }
        selectedText={
          this.state.allowEqCreationViaSelection && this.state.selectedText
            ? this.state.selectedText
            : null
        }
        allowCopyPasteInEquationEditor={allowCopyPasteInEquationEditor}
        disabledCopyButtonTooltipText={disabledCopyButtonTooltipText}
      />
    );
  };

  componentDidMount() {
    docEditorStore.setState(true);
  }

  componentWillUnmount() {
    docEditorStore.setState(false);
  }

  handleUploadStateChange = uploadIds => {
    this.setState({ uploadIds });

    // Notify parent component of upload state change
    const { onUploadStateChange } = this.props;
    if (onUploadStateChange) {
      onUploadStateChange(uploadIds);
    }
  };

  canSafelyUnmount = onConfirm => {
    if (this.state.uploadIds.length > 0) {
      this.setState({
        showUploadConfirmation: true,
        pendingUnmountCallback: onConfirm,
      });
      return false;
    }
    return true;
  };

  // Returns true if upload is currently in progress
  isUploadInProgress = () => this.state.uploadIds.length > 0;

  // Call this when user confirms they want to discard the upload and close
  confirmDiscard = () => {
    this.setState({ uploadIds: [] });
  };

  renderEdit = () => {
    const {
      height,
      width = 100,
      value = "",
      placeholderText = "",
      viewOnly = false,
      saveDelay = 1000,
      containerStyle = {},
      onUploadMedia,
      isMathsEquationEnabled = true,
      i18n,
      minHeight = 100,
      styles = {},
      shouldEditorFocusOnLoad = false,
      isInitialContentChangeIgnored = false,
      hideErrorMessage = false,
      nodesDisabled = {},
      imageInsertionImperativeHandler,
      embedInsertionImperativeHandler,
      floatingMiniEditorToolbarConfig,
      isSlashMenuEnabled = true,
      editorClassName = "",
      singleLine = false,
      isPortalToBody = false,
    } = this.props;

    const errorMessage = this.state.error ? this.state.error : "";

    const handleMathsEditorOpen = () => {
      const equationEditorInstance =
        MiniDocEditorComponent.openEquationEditorInstance;
      if (
        !this.state.isEquationEditorOpen &&
        equationEditorInstance &&
        equationEditorInstance !== this
      ) {
        equationEditorInstance.closeEditor();
      }

      if (!this.state.isEquationEditorOpen) {
        MiniDocEditorComponent.openEquationEditorInstance = this;
      } else {
        MiniDocEditorComponent.openEquationEditorInstance = null;
      }
      this.setState(prevState => ({
        isEquationEditorOpen: !prevState.isEquationEditorOpen,
        latex: "",
      }));
    };

    const handleUpload = async (...args) => {
      try {
        const result = await this.props.uploadFileV2(...args);
        if (onUploadMedia) {
          onUploadMedia(result, ...args);
        }
        return result;
      } catch (e) {
        console.error("Error uploading file:", e);
      }
    };

    const handleClearEquation = () =>
      this.setState({
        equationAttributes: "",
      });

    const handleDoubleClick = latexProps => {
      const equationEditorInstance =
        MiniDocEditorComponent.openEquationEditorInstance;

      if (equationEditorInstance && equationEditorInstance !== this) {
        equationEditorInstance.closeEditor();
      }

      MiniDocEditorComponent.openEquationEditorInstance = this;
      this.setState({
        latex: latexProps,
        isEquationEditorOpen: true,
      });
    };

    const mathsEditorConfig = isMathsEquationEnabled && {
      isOpen: this.state.isEquationEditorOpen,
      setIsMathsEditorOpen: handleMathsEditorOpen,
      equationAttributes: this.state.equationAttributes,
      clearEquation: handleClearEquation,
      onDoubleClick: handleDoubleClick,
    };

    return (
      <div ref={this.containerRef} style={containerStyle}>
        <MiniDocEditor
          lang={i18n?.language || "en"}
          height={height}
          width={width}
          content={value}
          onChange={this.changeValue}
          onFocus={e => this.onFocus({ value })}
          onBlur={this._onBlur}
          uploadToServer={handleUpload}
          hasContent={value =>
            this.setState({
              hasContent: value,
            })
          }
          placeholderText={placeholderText}
          getCloudUrl={getCloudUrl}
          uploadFile={handleUpload}
          viewOnly={viewOnly}
          saveDelay={saveDelay}
          minHeight={minHeight}
          styles={styles}
          shouldEditorFocusOnLoad={shouldEditorFocusOnLoad}
          allowURLWithCredentials={allowURLWithCredentials}
          nodesDisabled={nodesDisabled}
          mathsEditorConfig={mathsEditorConfig}
          isInitialContentChangeIgnored={isInitialContentChangeIgnored}
          onUploadStateChange={this.handleUploadStateChange}
          imageInsertionImperativeHandler={imageInsertionImperativeHandler}
          embedInsertionImperativeHandler={embedInsertionImperativeHandler}
          floatingMiniEditorToolbarConfig={floatingMiniEditorToolbarConfig}
          isSlashMenuEnabled={isSlashMenuEnabled}
          className={editorClassName}
          singleLine={singleLine}
          isPortalToBody={isPortalToBody}
        />

        {isMathsEquationEnabled &&
          this.state.isEquationEditorOpen &&
          MiniDocEditorComponent.openEquationEditorInstance === this &&
          this.renderEquationEditor()}

        <FormInputError
          id={this.errorId}
          error={!hideErrorMessage && errorMessage ? errorMessage : null}
        />
      </div>
    );
  };
}

const mapActionCreators = {
  setToastMsg,
  uploadFileV2,
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  return {
    orgId: userInfo.org_id,
    userInfo,
  };
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, undefined, { withRef: true })
)(MiniDocEditorComponent);

MiniDocEditorComponent.propTypes = {
  ...UIBaseComponent.propTypes,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  error: PropTypes.string,
  placeholderText: PropTypes.string,

  // Behavior flags
  viewOnly: PropTypes.bool,
  hideErrorMessage: PropTypes.bool,
  shouldCallOnBlur: PropTypes.bool,

  // Delay in ms before editor auto-save
  saveDelay: PropTypes.number,
  shouldEditorFocusOnLoad: PropTypes.bool,
  isInitialContentChangeIgnored: PropTypes.bool,
  isMathsEquationEnabled: PropTypes.bool,
  isSlashMenuEnabled: PropTypes.bool,

  // Editor appearance
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  styles: PropTypes.object,
  containerStyle: PropTypes.object,

  // Upload / media handlers
  onUploadStateChange: PropTypes.func,
  onUploadMedia: PropTypes.func,

  // Imperative handlers
  imageInsertionImperativeHandler: PropTypes.func,
  embedInsertionImperativeHandler: PropTypes.func,
  floatingMiniEditorToolbarConfig: PropTypes.object,

  nodesDisabled: PropTypes.object,
  className: PropTypes.string,
  singleLine: PropTypes.bool,
  isPortalToBody: PropTypes.bool,
};

MiniDocEditorComponent.defaultProps = {
  ...UIBaseComponent.defaultProps,
  height: 350,
  width: 864,
  shouldCallOnBlur: true,
  hideErrorMessage: false,
  placeholderText: "",
  isActive: false,
  toolbarPosition: "top",
  isHeightAdaptive: false,
  isToolbarSticky: false,
  containerStyle: {},
  className: "",
  singleLine: false,
  isPortalToBody: false,
};
